import { LocationDescriptorObject } from 'history';
import { identity, isEqual, omitBy, pick, pickBy, random, uniq } from 'lodash';
import { Moment } from 'moment';
import { deflate, inflate } from 'pako';
import qs, { IParseOptions, IStringifyOptions } from 'qs';
import type { ImgixUrl } from 'typescript-imgix-url-params';

import ROUTES from '@agency/routes/routes.constant';
import { t } from '@core/i18n';
import { Option } from '@shared/components/select-new/Select';
import { PrimarySocietyGroup, Societies } from '@shared/constants/societies';
import { AppId } from '@shared/contexts/ConfigContext';
import { ConfigAgencyConstants } from '@shared/models/config';
import { IUser, IUserSociety } from '@shared/models/IUser';
import { ITheme } from '@shared/models/theme';
import { BuildingTypeGroups } from '@shared/types/common/building';
import { NewButtonColour } from '@shared/types/common/button';
import { history } from '@shared/utils/history';

export const arrayToCsv = (arr?: any[], sort = false): string => {
  if (!Array.isArray(arr) || !arr.length) {
    return '';
  }

  // Pluck out the lengthy values from the array
  const items = arr.reduce<string[]>((result, item) => {
    if (typeof item === 'string' && item) {
      result.push(item);
    } else if (typeof item === 'number') {
      result.push(item.toString());
    }

    return result;
  }, []);

  // Return CSV string
  return sort ? items.sort().join(',') : items.join(',');
};

export const objectArrayToObject = <T extends Record<string, any>>(array: T[], key: keyof T): Record<string, T> => {
  if (!array?.length) {
    return {} as T;
  }

  return array.reduce((result, item) => {
    result[item[key]] = item;

    return result;
  }, {} as Record<string, T>);
};

export const objectArrayToCsv = (arr?: Record<string, any>[], key = '', sort = false): string => {
  if (!Array.isArray(arr) || !arr.length) {
    return '';
  }
  if (!key || !Object.prototype.hasOwnProperty.call(arr[0], key)) {
    return '';
  }

  // Pluck out the values from the array of objects
  const items = arr.reduce<string[]>((result, item) => {
    if (Object.prototype.hasOwnProperty.call(item, key)) {
      if (typeof item[key] === 'string' && item[key]) {
        result.push(item[key]);
      } else if (typeof item[key] === 'number') {
        result.push(item[key].toString());
      }
    }

    return result;
  }, []);

  // Return CSV string
  return sort ? items.sort().join(',') : items.join(',');
};

export const isNullable = (value: any) => {
  return typeof value === 'undefined' || value === 'undefined' || value === null || value === 'null';
};

export const isTruthy = (value: boolean | string | any[] | number | Moment | undefined | null): boolean => {
  if (typeof value === 'boolean') {
    return value;
  } else if (typeof value === 'string' || Array.isArray(value)) {
    return !!value.length;
  } else if (typeof value === 'number') {
    return value > 0;
  }

  return !!value;
};

export const getDefaultRoute = (): string => {
  return `${ROUTES.marketplace.insights.root}${ROUTES.marketplace.insights.acquisitions}`;
};

export function getCurrentUrl(pathname: string) {
  return pathname.split(/[?#]/)[0];
}

export function checkIsActive(pathname: string, url?: string) {
  const current = getCurrentUrl(pathname);
  if (!current || !url) {
    // console.log(`no current or url. pathname: "${pathname}", current: "${current}", url: "${url}"`);
    return false;
  }

  if (current === url) {
    // console.log(`current === url. pathname: "${pathname}", current: "${current}", url: "${url}"`);
    return true;
  }

  if (current.startsWith(url)) {
    // console.log(`current.startsWith. pathname: "${pathname}", current: "${current}", url: "${url}"`);
    return true;
  }

  // console.log(`false. pathname: "${pathname}", current: "${current}", url: "${url}"`);
  return false;
}

export const getDefaultError = (error?: string, altText?: string) => {
  return error || altText || 'Something went wrong. Please contact us for assistance.';
};

export const getQueries = (): ParsedQuery => {
  // console.log('getQueries. search: ', history.location.search);
  return parseQueries(history.location.search);
};

export const getQueryValueString = (key: string, queries?: Query, defaultValue = ''): string => {
  const query = queries || parseQueries(history.location.search);

  if (typeof query[key] === 'object' && query[key] && Array.isArray(query[key])) {
    return (query[key] as string[]).join(',');
  } else if (typeof query[key] === 'string' && query[key]) {
    return query[key] as string;
  }

  return defaultValue;
};

export const getQueryValueStringBoolean = (key: string, queries?: Query): string => {
  // For filters which accept string 'true' or 'false
  const query = queries || parseQueries(history.location.search);

  if (query[key] === 'true') {
    return '1';
  }

  if (query[key] === 'false') {
    return '0';
  }

  return '';
};

export const getQueryValueNumber = (key: string, queries?: Query): number => {
  const query = queries || parseQueries(history.location.search);
  const parsedValue = typeof query[key] === 'string' ? Number(query[key]) : 0;

  return !isNaN(parsedValue) ? parsedValue : 0;
};

// const initQueries = useMemo(() => getQueries(), []);

export const getQueryValueStringArray = (key: string, queries?: Query): string[] => {
  const query = queries || parseQueries(history.location.search);

  if (typeof query[key] === 'object' && query[key] && Array.isArray(query[key])) {
    return (query[key] as string[]).filter((item) => item);
  } else if (typeof query[key] === 'string' && query[key]) {
    return String(query[key])
      .split(',')
      .filter((item) => item);
  }

  return [];
};

export const getQueryValueNumberArray = (key: string, queries?: Query): number[] =>
  getQueryValueStringArray(key, queries).map((item) => parseInt(item));

export const cleanQueries = (query: PartialQuery, whitelistEmpty: string[] = []): ParsedQuery => {
  return Object.keys(query).reduce((result, key) => {
    const queryValue = query[key];

    if (typeof queryValue === 'string' && (queryValue || whitelistEmpty.includes(key))) {
      result[key] = queryValue;
    } else if (Array.isArray(queryValue) && (queryValue.length || whitelistEmpty.includes(key))) {
      result[key] = queryValue;
    }

    return result;
  }, {} as ParsedQuery);
};

export const parseQueries = (search?: QueryString, options?: IParseOptions): ParsedQuery => {
  if (!search) {
    return {};
  }

  return qs.parse(search, {
    arrayLimit: Infinity,
    ignoreQueryPrefix: true,
    interpretNumericEntities: true,
    decoder: (str) => decodeURIComponent(str),
    ...options,
  }) as ParsedQuery;
};

export const pickQueries = <T = unknown>(queries: Query, keys: readonly any[]) => pick<Query>(queries, keys) as T;

export const getQueriesAsSearch = (queries?: any, encode = true): string => getQueriesAsSearchNew(queries, { encode });

export const filterEmptyQueryParams = (queries: Query): Query => {
  // Filter out any keys with empty values
  const filteredQueries = queries
    ? pickBy(queries, (value) => {
        // Keep boolean values
        if (typeof value === 'boolean') {
          return true;
        }
        // Keep non-empty string values
        if (typeof value === 'string') {
          return value.length > 0;
        }
        // Keep arrays with content
        if (Array.isArray(value)) {
          return value.length > 0;
        }
        // Keep other non-null, non-undefined values
        return value !== null && value !== undefined;
      })
    : {};

  return filteredQueries;
};
export const getQueriesAsSearchNew = (queries?: any, options: IStringifyOptions = {}): string => {
  return qs.stringify(queries, {
    addQueryPrefix: true,
    encode: true,
    skipNulls: true,
    sort: (a, b) => a.localeCompare(b),
    ...options,
  });
};

export const isQueryValueTruthy = (value: any) => {
  return !isNullable(value) && value !== false;
};

export const setQueries = (
  queries: Query,
  replace?: boolean,
  locationArgs?: Partial<LocationDescriptorObject>,
  encode = true
) => {
  const args = {
    search: getQueriesAsSearch(
      omitBy(queries, (value) => !value),
      encode
    ),
    ...locationArgs,
  };

  if (replace) {
    // console.log('setQueries. replace: ', { ...args });
    return history.replace(args);
  }

  // console.log('setQueries. push: ', { ...args });
  return history.push(args);
};

export const deleteQueries = (
  queriesToDelete?: string[],
  replace?: boolean,
  locationArgs?: Partial<LocationDescriptorObject>
) => {
  if (!queriesToDelete) {
    return setQueries({}, replace, locationArgs);
  }

  const currentQueries = getQueries();
  const finalQueries = omitBy(currentQueries, (value, key) => queriesToDelete.includes(key));

  setQueries(finalQueries, replace, locationArgs);
};

export interface GetQueriesCountData {
  blacklistKeys?: string[];
  groupKeys?: string[][];
  queries?: ParsedQuery;
  whitelistKeys?: string[];
}

export const getQueriesCount = ({
  blacklistKeys = [],
  groupKeys = [],
  queries,
  whitelistKeys = [],
}: GetQueriesCountData): number => {
  if (!queries) {
    // console.log('getQueriesCount. params do not exist: ', queries);
    return 0;
  }

  let filteredKeys = whitelistKeys;
  if (whitelistKeys.length && blacklistKeys.length) {
    filteredKeys = whitelistKeys.filter((key) => !blacklistKeys.includes(key));
  }

  const queryKeys = Object.getOwnPropertyNames(queries).filter((key) =>
    filteredKeys.length ? filteredKeys.includes(key) : true
  );

  if (!queryKeys.length) {
    // console.log('getQueriesCount. view params are empty: ', Object.keys(queries), 'whitelist: ', whitelistKeys);
    return 0;
  }

  const queryHasLengthyKey = (key: string) => Object.prototype.hasOwnProperty.call(queries, key) && queries[key];
  const flattenedGroupKeys = groupKeys.flat();
  let count = 0;

  // Single keys
  queryKeys
    .filter((key) => !flattenedGroupKeys.includes(key))
    .forEach((key) => {
      // console.log(`getQueriesCount. checking single key "${key}": `, queries[key]);
      if (queryHasLengthyKey(key)) {
        count++;
      }
    });

  // Group keys
  groupKeys.forEach((group) => {
    // console.log(`getQueriesCount. checking group: `, group, queries);
    if (group.some((key) => queryKeys.includes(key) && queryHasLengthyKey(key))) {
      count++;
    }
  });

  return count;
};

export const getImgixUrl = (url: string, imgixParams: ImgixUrl.Params = {}): string => {
  const urlParts = url.split('?');
  const path = urlParts[0] ?? '';
  const queryString = urlParts[1] ?? '';

  const params = {
    ...(queryString && parseQueries(queryString)),
    ...imgixParams,
  };

  return Object.keys(params).length ? `${path}?${qs.stringify(params)}` : path;
};

export const compressString = (value: string): string => {
  // Compress string to Uint8Array
  const compressedUint8Array = deflate(value);

  // Convert Uint8Array to Base64-encoded string
  return btoa(String.fromCharCode.apply(null, Array.from(compressedUint8Array)));
};

export const decompressString = (compressedValue: string): string => {
  // Convert Base64-encoded string to Uint8Array
  const compressedUint8Array = new Uint8Array(
    atob(compressedValue)
      .split('')
      .map((char) => char.charCodeAt(0))
  );

  // Decode Uint8Array
  const decompressedUint8Array = inflate(compressedUint8Array);

  // Convert Uint8Array to string
  return new TextDecoder().decode(decompressedUint8Array);
};

export const generateFeatureId = () => {
  return Math.random().toString(36).replace('0.', '');
};

export const removeEmptyQueries = (query: any) => pickBy(query, identity);

export const pickQueryKeys = (query: ParsedQuery | undefined, keys: string[]): ParsedQuery | undefined => {
  if (!query) {
    return undefined;
  }

  const result = pick(query, keys);

  return Object.keys(result).length > 0 ? result : undefined;
};

export const getInitials = (fullName: string) =>
  fullName
    .split(' ')
    .splice(0, 2)
    .map((n) => n[0]?.toUpperCase())
    .join('')
    .replace(/ +(?= )/g, '');

export const valueToYesOrNo = (value?: any) => {
  if (value && Number(value) == 1) {
    return t('yes');
  }

  if (value == null) {
    return '-';
  }

  return t('no');
};

export const getBuildingTypeState = (buildingTypes: string | any[], constants: ConfigAgencyConstants) => {
  const buildingTypesMap = constants.buildingTypeGroups;
  const state = {
    hasCommercialLandType: false,
    hasIndustrialType: false,
    hasOfficeType: false,
    hasOtherType: false,
    hasRetailType: false,
    values: [],
  };

  if (!buildingTypesMap) {
    return state;
  }

  // Wrap single building type in an array
  if (typeof buildingTypes === 'string' && buildingTypes) {
    buildingTypes = [buildingTypes];
  }

  if (Array.isArray(buildingTypes)) {
    buildingTypes.forEach(function (item) {
      const buildingTypeValue = typeof item === 'object' ? item.value : item;

      if (
        !state.hasCommercialLandType &&
        buildingTypesMap[BuildingTypeGroups.commercialLand].keys.indexOf(buildingTypeValue) !== -1
      ) {
        state.hasCommercialLandType = true;
      }
      if (
        !state.hasIndustrialType &&
        buildingTypesMap[BuildingTypeGroups.industrial].keys.indexOf(buildingTypeValue) !== -1
      ) {
        state.hasIndustrialType = true;
      }
      if (!state.hasOfficeType && buildingTypesMap[BuildingTypeGroups.office].keys.indexOf(buildingTypeValue) !== -1) {
        state.hasOfficeType = true;
      }
      if (!state.hasOtherType && buildingTypesMap[BuildingTypeGroups.other].keys.indexOf(buildingTypeValue) !== -1) {
        state.hasOtherType = true;
      }
      if (!state.hasRetailType && buildingTypesMap[BuildingTypeGroups.retail].keys.indexOf(buildingTypeValue) !== -1) {
        state.hasRetailType = true;
      }

      // @ts-ignore
      state.values.push(buildingTypeValue);
    });
  }

  // console.log('buildingTypes: ', buildingTypes);
  // console.log('building types state: ', state);
  return state;
};

export const buildingTypesOptionsToString = (buildingTypes: string | string[], options: Option[]): string => {
  // Wrap single building type in an array
  if (typeof buildingTypes === 'string' && buildingTypes) {
    buildingTypes = [buildingTypes];
  }

  const labels: string[] = [];

  if (Array.isArray(buildingTypes)) {
    buildingTypes.forEach((value) => {
      options.forEach((option) => {
        if (option.id == value) {
          labels.push(option.label);
        }
      });
    });
  }

  return labels.join(', ') || '-';
};

export const getSocietiesState = (user: IUser) => {
  // Default society filters
  // let societyFilters = typeof user?.society_filters === 'object' ? user.society_filters : {};

  // Society data
  let primaryGroup: PrimarySocietyGroup | null = null;

  let oasSocietyGroups: IUserSociety['groups'] = [];
  const societyGroupIds: number[] = [];
  const societyIds: Societies[] = [];
  const societyIdsGroupHash: { [key: number]: Societies } = {};
  let isWEASMember = false;
  let isCACMember = false;
  let isOASMember = false;
  let isIASMember = false;
  let isLOAFMember = false;
  let isRLASMember = false;

  if (user.societies) {
    for (const society of user.societies) {
      // Populate the society lists
      societyIds.push(society.id);

      // Add to the list of society group ids
      for (const group of society.groups) {
        societyGroupIds.push(group.id);
        societyIdsGroupHash[group.id] = society.id;

        // Store the first primary group id
        if (!primaryGroup && group.primary) {
          primaryGroup = {
            id: group.id,
            society: society.id,
            filterByGroup: society.id === Societies.OAS,
          };
        }
      }

      // Set flags
      if (society.id === Societies.WEAS) {
        isWEASMember = true;
      } else if (society.id === Societies.CAC) {
        isCACMember = true;
      } else if (society.id === Societies.OAS) {
        isOASMember = true;
        oasSocietyGroups = society.groups;
      } else if (society.id === Societies.IAS) {
        isIASMember = true;
      } else if (society.id === Societies.LOAF) {
        isLOAFMember = true;
      } else if (society.id === Societies.RLAS) {
        isRLASMember = true;
      }
    }
  }

  return {
    isCACMember,
    isCACOnly: isCACMember && user.societies?.length === 1,
    isIASAndAnotherMember: isIASMember && (isWEASMember || isCACMember || isOASMember || isLOAFMember || isRLASMember),
    isIASMember,
    isIASOnly: isIASMember && user.societies?.length === 1,
    isOASMember,
    isOASOnly: isOASMember && user.societies?.length === 1,
    isRLASMember,
    isRLASOnly: isRLASMember && user.societies?.length === 1,
    isLOAFMember,
    isLOAFOnly: isLOAFMember && user.societies?.length === 1,
    isWEASMember,
    isWEASOnly: isWEASMember && user.societies?.length === 1,
    multipleSocieties: (user.societies?.length || 0) > 1,
    oasSocietyGroups,
    primaryGroup,
    societyGroupIds,
    societyIdsGroupHash,
    societyIds,
  };
};

export const serialize = (data: any) => {
  return data ? JSON.stringify(data) : '';
};

export const deserialize = (str = '', defaultValue: any = {}) => {
  if (!str) {
    return defaultValue;
  }

  try {
    return JSON.parse(str);
  } catch {
    return defaultValue;
  }
};

export const getDataFromStorage = (storageKey: StorageKey): { [key: string]: any } | any => {
  return deserialize(localStorage.getItem(String(storageKey)) || '', '');
};

export const getStringFromStorage = (storageKey: StorageKey): string => {
  return (localStorage.getItem(String(storageKey)) as string) || '';
};

export const setDataToStorage = (storageKey: StorageKey, data?: any) => {
  return localStorage.setItem(String(storageKey), serialize(data));
};

export const setStringIntoStorage = (storageKey: StorageKey, value?: string) => {
  return localStorage.setItem(String(storageKey), value || '');
};

export const deleteStorage = (storageKey: StorageKey) => {
  localStorage.removeItem(String(storageKey));
};

export const getElementOuterHeight = (node: Element | null) => {
  if (!node) {
    return 0;
  }

  const styles = window.getComputedStyle(node);
  const rect = node.getBoundingClientRect();
  return parseInt(styles.marginTop) + rect.height + parseInt(styles.marginBottom);
};

export const filterCsv = (csv: string, whitelist: string[]) => {
  if (!whitelist.length || !csv) {
    return csv;
  }

  return csv
    .split(',')
    .filter((key) => key && whitelist.includes(key))
    .join(',');
};

export const forStringRecord = <TRecordValue = any, TRecordKey extends string = string>(
  record: Record<TRecordKey, TRecordValue>,
  callback: (value: TRecordValue, key: TRecordKey) => void
): void => {
  (Object.getOwnPropertyNames(record) as TRecordKey[]).forEach((key: TRecordKey) => callback(record[key], key));
};

export const forNumberRecord = <TRecordValue = any, TRecordKey extends number = number>(
  record: Record<TRecordKey, TRecordValue>,
  callback: (value: TRecordValue, key: TRecordKey) => void
): void => {
  (Object.getOwnPropertyNames(record).map(Number) as TRecordKey[]).forEach((key: TRecordKey) =>
    callback(record[key], key)
  );
};

export const mapStringRecord = <TRecordValue = any, TRecordKey extends string = string>(
  record: Record<TRecordKey, TRecordValue>,
  callback: (value: TRecordValue, key: TRecordKey) => any
): any[] => {
  return (Object.getOwnPropertyNames(record) as TRecordKey[]).map((key: TRecordKey) => callback(record[key], key));
};

export const mapNumberRecord = <TRecordValue = any, TRecordKey extends number = number>(
  record: Record<TRecordKey, TRecordValue>,
  callback: (value: TRecordValue, key: TRecordKey) => any
): any[] => {
  return (Object.getOwnPropertyNames(record).map(Number) as TRecordKey[]).map((key: TRecordKey) =>
    callback(record[key], key)
  );
};

export const reduceStringRecordToObject = <TRecordValue = any, TNewValue = any, TRecordKey extends string = string>(
  record: Record<TRecordKey, TRecordValue>,
  callback: (result: TNewValue, value: TRecordValue, key: TRecordKey) => TNewValue
): TNewValue =>
  (Object.getOwnPropertyNames(record) as TRecordKey[]).reduce<TNewValue>(
    (result, key) => callback(result, record[key], key),
    {} as TNewValue
  );

export const reduceNumberRecordToObject = <TRecordValue = any, TNewValue = any, TRecordKey extends number = number>(
  record: Record<TRecordKey, TRecordValue>,
  callback: (result: TNewValue, value: TRecordValue, key: TRecordKey) => TNewValue
): TNewValue =>
  (Object.getOwnPropertyNames(record).map(Number) as TRecordKey[]).reduce<TNewValue>(
    (result, key) => callback(result, record[key], key),
    {} as TNewValue
  );

export const reduceStringRecordToArray = <TRecordValue = any, TNewValue = any, TRecordKey extends string = string>(
  record: Record<TRecordKey, TRecordValue>,
  callback: (result: TNewValue[], value: TRecordValue, key: TRecordKey) => TNewValue[]
): TNewValue[] =>
  (Object.getOwnPropertyNames(record) as TRecordKey[]).reduce<TNewValue[]>(
    (result, key) => callback(result, record[key], key),
    [] as TNewValue[]
  );

export const reduceStringRecordToSortedArray = <
  TRecordValue = any,
  TNewValue = any,
  TRecordKey extends string = string
>(
  record: Record<TRecordKey, TRecordValue>,
  callback: (result: TNewValue[], value: TRecordValue, key: TRecordKey) => TNewValue[]
): TNewValue[] =>
  (Object.getOwnPropertyNames(record) as TRecordKey[])
    .sort()
    .reduce<TNewValue[]>((result, key) => callback(result, record[key], key), [] as TNewValue[]);

export const reduceNumberRecordToArray = <TRecordValue = any, TNewValue = any, TRecordKey extends number = number>(
  record: Record<TRecordKey, TRecordValue>,
  callback: (result: TNewValue[], value: TRecordValue, key: TRecordKey) => TNewValue[]
): TNewValue[] =>
  (Object.getOwnPropertyNames(record).map(Number) as TRecordKey[]).reduce<TNewValue[]>(
    (result, key) => callback(result, record[key], key),
    [] as TNewValue[]
  );

export const reduceIdRecordToArray = <TRecordValue = any, TNewValue = any, TRecordKey extends Id = Id>(
  record: Record<TRecordKey, TRecordValue>,
  callback: (result: TNewValue[], value: TRecordValue, key: TRecordKey) => TNewValue[]
): TNewValue[] =>
  (Object.getOwnPropertyNames(record).map(Number) as TRecordKey[]).reduce<TNewValue[]>(
    (result, key) => callback(result, record[key], key),
    [] as TNewValue[]
  );

export const someStringRecord = <TRecordValue = any, TRecordKey extends string = string>(
  record: Record<TRecordKey, TRecordValue>,
  callback: (value: TRecordValue, key: TRecordKey) => boolean
): boolean =>
  (Object.getOwnPropertyNames(record) as TRecordKey[]).some((key: TRecordKey) => callback(record[key], key));

export const someNumberRecord = <TRecordValue = any, TRecordKey extends number = number>(
  record: Record<TRecordKey, TRecordValue>,
  callback: (value: TRecordValue, key: TRecordKey) => boolean
): boolean =>
  (Object.getOwnPropertyNames(record).map(Number) as TRecordKey[]).some((key: TRecordKey) =>
    callback(record[key], key)
  );

export const filterStringRecordKeys = <TRecordValue = any, TRecordKey extends string = string>(
  record: Record<TRecordKey, TRecordValue>,
  callback: (value: TRecordValue, key: TRecordKey) => boolean
): TRecordKey[] =>
  (Object.getOwnPropertyNames(record) as TRecordKey[]).filter((key: TRecordKey) => callback(record[key], key));

export const filterNumberRecordKeys = <TRecordValue = any, TRecordKey extends number = number>(
  record: Record<TRecordKey, TRecordValue>,
  callback: (value: TRecordValue, key: TRecordKey) => boolean
): TRecordKey[] =>
  (Object.getOwnPropertyNames(record).map(Number) as TRecordKey[]).filter((key: TRecordKey) =>
    callback(record[key], key)
  );

export const getObjectDiff = <TData extends Record<string, any> = Record<string, any>>(
  obj1: TData,
  obj2: TData,
  blacklistKeys?: (keyof TData)[]
): Partial<TData> => {
  const diff: Partial<TData> = {};

  const uniqueKeys = uniq([...Object.keys(obj1), Object.keys(obj2)]);

  for (const stringKey of uniqueKeys) {
    const key = stringKey as keyof TData;

    if (blacklistKeys?.includes(key)) {
      continue;
    }

    const obj1KeyExists = Object.prototype.hasOwnProperty.call(obj1, key);
    const obj2KeyExists = Object.prototype.hasOwnProperty.call(obj2, key);

    // Added new key to obj 2
    if (!obj1KeyExists && obj2KeyExists) {
      diff[key] = obj2[key];
      continue;
    }

    // Value for key has changed
    if (obj1KeyExists && obj2KeyExists && !isEqual(obj1[key], obj2[key])) {
      diff[key] = obj2[key];
      continue;
    }
  }

  return diff;
};

export const getBuildingTypesString = (buildingTypes: string[] = [], buildingTypeOptions: Option[]) => {
  let text = '';
  let delim = '';

  buildingTypes.forEach((type: string) => {
    buildingTypeOptions.forEach((option) => {
      if (type === option.id) {
        text += delim + option.label;
        delim = ', ';
      }
    });
  });
  return text;
};

export const toOptionalBoolean = (value?: null | boolean | number | string): boolean | undefined => {
  if (typeof value === 'boolean') {
    return value;
  } else if (typeof value === 'number') {
    if (value === 1) {
      return true;
    } else if (value === 0) {
      return false;
    }
  } else if (typeof value === 'string' && value) {
    if (value === '1' || value === 'true') {
      return true;
    } else if (value === '0' || value === 'false') {
      return false;
    }
  }

  return undefined;
};

export const toNullableBoolean = (value?: null | boolean | number | string): boolean | null => {
  return toOptionalBoolean(value) ?? null;
};

export const toNullableNumberBoolean = (value?: null | boolean | number | string): NumberBoolean | null => {
  const result = toNullableBoolean(value);

  return typeof result === 'boolean' ? (result ? 1 : 0) : null;
};

export const toOptionalNumberBoolean = (value?: null | boolean | number | string): NumberBoolean | undefined => {
  const result = toOptionalBoolean(value);

  return typeof result === 'boolean' ? (result ? 1 : 0) : undefined;
};

export const toNullableStringBoolean = (value?: null | boolean | number | string): StringBoolean | null => {
  const result = toNullableBoolean(value);

  return typeof result === 'boolean' ? (result ? '1' : '0') : null;
};

export const toOptionalStringBoolean = (value?: null | boolean | number | string): StringBoolean | undefined => {
  const result = toOptionalBoolean(value);

  return typeof result === 'boolean' ? (result ? '1' : '0') : undefined;
};

export const generateRandomKey = (): string => {
  return random(0, 100000000000).toString();
};

export const getButtonColour = (colour: NewButtonColour, appId: AppId): NewButtonColour => {
  // Return the correct colour depending on app context
  if (colour === NewButtonColour.PrimarySpecial) {
    switch (appId) {
      case AppId.Agency:
        return NewButtonColour.PrimarySalmon;
      case AppId.Landlord:
        return NewButtonColour.PrimaryTeal;
      case AppId.Atlas:
        return NewButtonColour.PrimaryLilac;
    }
  }

  return colour;
};

// TODO: Colour codes is copied from same BE util. Codes not exist in out colours constants yet
const nameThemes: [ITheme, ITheme, ITheme, ITheme, ITheme] = [
  { backgroundColour: '#CFDDFA', textColour: '#487EF5' },
  { backgroundColour: '#FEE0D1', textColour: '#FF8750' },
  { backgroundColour: '#C9F3DE', textColour: '#31D482' },
  { backgroundColour: '#ECD7EF', textColour: '#BA68C8' },
  { backgroundColour: '#FEEDBF', textColour: '#FFBF05' },
];

export const getThemeFromName = (fullName: string): ITheme => {
  const char = fullName?.charAt(0) || '';
  const charCode = char.toLowerCase().charCodeAt(0) - 96;

  if (charCode < 5) {
    return nameThemes[0];
  } else if (charCode < 10) {
    return nameThemes[1];
  } else if (charCode < 15) {
    return nameThemes[2];
  } else if (charCode < 20) {
    return nameThemes[3];
  }

  return nameThemes[4];
};

export const calculateImageWithRatio = (width: number): { width: number; height: number } => {
  const ratio = 16 / 9;
  const height = Math.round(width / ratio);

  return { width, height };
};
